import * as Sentry from '@sentry/browser'
import { GoogleLogin, CredentialResponse, GoogleOAuthProvider } from '@react-oauth/google'
import { useState } from 'react'
import { useToast } from '@homebotapp/hb-react-component-catalog'
import cookies from 'js-cookie'
import LoadingPanel from '../../Components/shared/Loading/LoadingBlock'
import { AUTH } from '../../constants'
import { MikasaCustomerDisplay } from '../../api/mikasa/type/mikasa_customer_display'
import { SelectCustomerProfile } from '../SelectCustomerProfile/SelectCustomerProfile'
import { UNAUTH_CLIENT_PATHS, unauthClient } from '../../api/lockbox'
import { getAllCustomers } from '../../api/mikasa'
import { getMagicLink } from '../../api/mikasa/magicLinkRequests'
import { getClientData } from '../../api/mikasa/requests'
import { setAgentToken } from '../../auth/widget'
import { AUTH_COOKIE_SETTINGS, clearClientToken } from '../../auth'
import { getHostname } from '../../helpers/browser'

// These are errors that we want to expose to the user
const ERRORS_TO_EXPOSE = ['user-not-found']

// These are purely user's errors, we don't want to capture them in Sentry
const ERRORS_TO_SKIP_CAPTURING = ['user-not-found']

const GENERIC_ERROR_MESSAGE = 'An error occurred during the login process. Please try again later.'

export function GoogleLoginButton() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID
  if (!googleClientId) {
    throw new Error('Missing Google Client ID. Please set the REACT_APP_GOOGLE_CLIENT_ID environment variable.')
  }
  const [profiles, setProfiles] = useState<MikasaCustomerDisplay[]>([])
  const [modalOpen, setModalOpen] = useState(false)
  const [omniToken, setOmniToken] = useState<string | null>(null)
  const [isLoadingExchangeOmniToken, setIsLoadingExchangeOmniToken] = useState(false)
  const [isLoadingExchangeIdToken, setIsLoadingExchangeIdToken] = useState(false)

  const toast = useToast()

  const handleError = (error: unknown) => {
    const message = error instanceof Error ? error.message : JSON.stringify(error)

    if (!ERRORS_TO_SKIP_CAPTURING.includes(message)) {
      const errorToCapture = error instanceof Error ? error : new Error(message)

      Sentry.captureException(errorToCapture, {
        extra: {
          source: 'GoogleLoginButton'
        }
      })
    }

    const title = ERRORS_TO_EXPOSE.includes(message) ? message : GENERIC_ERROR_MESSAGE

    toast({
      title,
      status: 'error'
    })
  }

  const handleSuccess = async ({ credential: idToken }: CredentialResponse) => {
    try {
      if (!idToken) {
        throw new Error('googleLoginButton_NoIdTokenReceived')
      }

      // Reset state after getting a new ID token
      setOmniToken(null)
      setProfiles([])
      clearClientToken()

      const exchangeIdToken = {
        path: UNAUTH_CLIENT_PATHS.EXCHANGE_ID_TOKEN,
        idToken
      }

      setIsLoadingExchangeIdToken(true)

      const response = await unauthClient(exchangeIdToken)

      // Very unlikely, only some bugs introduced to lockbox API
      if (!(response && typeof response === 'object' && 'token' in response)) {
        throw new Error('Invalid response from unauthClient:')
      }

      const { token: omniToken } = response

      if (typeof omniToken !== 'string') {
        throw new Error('Invalid Omni Token received from unauthClient')
      }

      setOmniToken(omniToken)

      // TODO: Shall we use `localStorage` or getSafeLocalStorage() here?
      localStorage?.setItem(AUTH.OMNI_TOKEN_KEY, omniToken)

      const profiles = await getAllCustomers(omniToken)

      if (!profiles?.length) {
        throw new Error('No customer profiles found')
      }

      // If there's only one profile, we just grab its clientId and set it
      if (profiles.length === 1) {
        handleSelectProfile(profiles[0], omniToken)
        return
      }

      setProfiles(profiles)
      setModalOpen(true)
    } catch (error) {
      handleError(error)
    } finally {
      setIsLoadingExchangeIdToken(false)
    }
  }

  const handleSelectProfile = async (selectedProfile: MikasaCustomerDisplay, omniTokenPassedIn?: string) => {
    try {
      const omniTokenToUse = omniTokenPassedIn || omniToken

      if (!omniTokenToUse || !selectedProfile.clientId) {
        return
      }

      setIsLoadingExchangeOmniToken(true)

      const exchangeOmniTokenInput = {
        clientId: selectedProfile.clientId,
        omniToken: omniTokenToUse,
        path: UNAUTH_CLIENT_PATHS.EXCHANGE_OMNI_TOKEN
      }

      const exchangeOmniTokenResponse = await unauthClient(exchangeOmniTokenInput)

      if (
        !exchangeOmniTokenResponse ||
        typeof exchangeOmniTokenResponse !== 'object' ||
        !('token' in exchangeOmniTokenResponse)
      ) {
        throw new Error('Invalid access token received from unauthClient')
      }

      const { token: accessToken } = exchangeOmniTokenResponse

      if (typeof accessToken !== 'string') {
        throw new Error('Invalid Access Token received from unauthClient')
      }

      cookies.set(AUTH.JWT_SESSION_COOKIE, accessToken, AUTH_COOKIE_SETTINGS)

      const clientData = await getClientData(selectedProfile.clientId, true)

      if (!clientData?.data?.data) {
        throw new Error('Invalid client data received from getClientData')
      }

      setAgentToken(clientData.data.data.customerProfile.publicKey)

      const magicLink = await getMagicLink(clientData.data.data.email)

      const reportUrl = magicLink?.data?.reportUrl

      if (typeof reportUrl !== 'string') {
        throw new Error('Invalid magic link received from getMagicLink')
      }

      // To make Google login work in development, when we use `localhost`
      const href =
        getHostname() === 'localhost' ? reportUrl.replace('https://c.homebot.test', 'http://localhost:4000') : reportUrl

      const hrefSplitAtHash = href.split('#')

      window.location.href =
        hrefSplitAtHash[0] +
        '&utm_source=login_page&utm_campaign=google_login&utm_medium=login_with_google' +
        '#' +
        hrefSplitAtHash[1]
    } catch (error) {
      handleError(error)
    } finally {
      setIsLoadingExchangeOmniToken(false)
    }
  }

  const isLoading = isLoadingExchangeOmniToken || isLoadingExchangeIdToken

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      {isLoading ? (
        <LoadingPanel />
      ) : (
        <GoogleLogin onSuccess={handleSuccess} onError={() => handleError(new Error('googleLoginFailed'))} />
      )}
      <SelectCustomerProfile
        profiles={profiles}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSelect={handleSelectProfile}
      />
    </GoogleOAuthProvider>
  )
}
